import React, { useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiMail,
  FiMessageSquare,
  FiSend,
  FiShield,
  FiUser,
} from "react-icons/fi";

import Reveal from "../../components/ui/Reveal";
import { saveMessageContactForm } from "../../services/contactformServices";

const INITIAL_FORM = {
  name: "",
  email: "",
  message: "",
  company: "", // honeypot anti-spam invisible
};

function isValidEmail(email = "") {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
}

export default function FormContact() {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [feedback, setFeedback] = useState("");

  const cleanEmail = useMemo(
    () => formData.email.trim().toLowerCase(),
    [formData.email],
  );

  const cleanName = useMemo(() => formData.name.trim(), [formData.name]);
  const cleanMessage = useMemo(
    () => formData.message.trim(),
    [formData.message],
  );

  const canSubmit =
    cleanName.length >= 2 &&
    isValidEmail(cleanEmail) &&
    cleanMessage.length >= 8 &&
    status !== "loading";

  const messageLength = cleanMessage.length;

  const updateField = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    if (status === "error" || status === "success") {
      setStatus("idle");
      setFeedback("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setFeedback("");

    if (formData.company) {
      return;
    }

    if (cleanName.length < 2) {
      setStatus("error");
      setFeedback("Veuillez renseigner votre nom et prénom.");
      return;
    }

    if (!isValidEmail(cleanEmail)) {
      setStatus("error");
      setFeedback("Veuillez saisir une adresse email valide.");
      return;
    }

    if (cleanMessage.length < 8) {
      setStatus("error");
      setFeedback("Votre message doit contenir au moins 8 caractères.");
      return;
    }

    setStatus("loading");

    try {
      await saveMessageContactForm({
        name: cleanName,
        email: cleanEmail,
        message: cleanMessage,
      });

      setStatus("success");
      setFeedback("Votre message a bien été envoyé. La mairie vous répondra dès que possible.");
      setFormData(INITIAL_FORM);
    } catch (error) {
      console.error("Erreur lors de l'envoi du message de contact :", error);

      setStatus("error");
      setFeedback(
        error?.response?.data?.message ||
          "Une erreur est survenue pendant l’envoi du message. Veuillez réessayer.",
      );
    }
  };

  return (
    <section
      id="contact"
      data-contact-section
      className="relative scroll-mt-28 overflow-hidden bg-slate-50 px-4 py-14 text-slate-950 dark:bg-slate-950 dark:text-white sm:px-6 lg:px-8 lg:py-20"
      aria-labelledby="contact-home-title"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-sky-300/15 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute -right-24 bottom-12 h-80 w-80 rounded-full bg-emerald-300/10 blur-3xl dark:bg-emerald-500/10" />
      </div>

      <div className="relative mx-auto max-w-[1380px]">
        <div className="mb-8 flex flex-col gap-5 md:mb-10 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                <span className="h-2.5 w-2.5 rounded-full bg-[#1277B9]" />
                Contact mairie
              </div>

              <h2
                id="contact-home-title"
                className="text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-4xl lg:text-5xl"
              >
                Formulaire de contact
              </h2>

              <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600 dark:text-slate-300">
                Vous avez une question, une remarque ou une demande à transmettre
                aux services municipaux ? Envoyez votre message à la mairie.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <div className="relative overflow-hidden rounded-[2.2rem] border border-slate-200 bg-white shadow-xl shadow-slate-950/5 dark:border-slate-800 dark:bg-slate-950">
            <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#1277B9] via-sky-400 to-emerald-300" />

            <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#1277B9]/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 left-1/3 h-52 w-52 rounded-full bg-emerald-400/10 blur-3xl" />

            <div className="relative grid gap-8 p-5 sm:p-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-start lg:p-8 xl:p-10">
              <aside className="rounded-[2rem] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900/70 sm:p-6">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1277B9] text-white shadow-lg shadow-sky-950/20">
                  <FiMail className="h-6 w-6" />
                </div>

                <h3 className="mt-6 text-2xl font-bold text-slate-950 dark:text-white">
                  La mairie à votre écoute
                </h3>

                <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
                  Votre message sera transmis aux services municipaux. Pensez à
                  indiquer clairement l’objet de votre demande afin de faciliter
                  le traitement.
                </p>

                <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-white p-4 text-sm leading-7 text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#1277B9]/10 text-[#1277B9] dark:bg-sky-500/15 dark:text-sky-300">
                      <FiShield className="h-5 w-5" />
                    </span>

                    <p>
                      Les informations saisies sont utilisées uniquement pour
                      répondre à votre demande de contact.
                    </p>
                  </div>
                </div>
              </aside>

              <form
                onSubmit={handleSubmit}
                className="rounded-[2rem] border border-slate-200 bg-slate-50 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/70 sm:p-5 md:p-6"
              >
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={updateField}
                  tabIndex={-1}
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                />

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="name"
                      className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400"
                    >
                      <FiUser className="h-4 w-4" />
                      Nom Prénom
                    </label>

                    <div className="relative mt-3">
                      <FiUser className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={updateField}
                        placeholder="Votre nom et prénom"
                        autoComplete="name"
                        required
                        className="min-h-[52px] w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#1277B9] focus:ring-4 focus:ring-[#1277B9]/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400"
                    >
                      <FiMail className="h-4 w-4" />
                      Votre email
                    </label>

                    <div className="relative mt-3">
                      <FiMail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={updateField}
                        placeholder="exemple@email.fr"
                        autoComplete="email"
                        required
                        className="min-h-[52px] w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#1277B9] focus:ring-4 focus:ring-[#1277B9]/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-5">
                  <div className="flex items-center justify-between gap-3">
                    <label
                      htmlFor="message"
                      className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400"
                    >
                      <FiMessageSquare className="h-4 w-4" />
                      Votre message
                    </label>

                    <span className="text-xs font-semibold text-slate-400">
                      {messageLength} caractère{messageLength > 1 ? "s" : ""}
                    </span>
                  </div>

                  <textarea
                    id="message"
                    name="message"
                    rows="7"
                    value={formData.message}
                    onChange={updateField}
                    placeholder="Expliquez votre demande ici..."
                    required
                    className="mt-3 w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm font-semibold leading-7 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#1277B9] focus:ring-4 focus:ring-[#1277B9]/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
                  />
                </div>

                {feedback && (
                  <div
                    className={[
                      "mt-5 rounded-2xl border px-4 py-3 text-sm font-semibold",
                      status === "success"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                        : "border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300",
                    ].join(" ")}
                    role="status"
                  >
                    <div className="flex items-start gap-2">
                      {status === "success" ? (
                        <FiCheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      ) : (
                        <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      )}

                      <span>{feedback}</span>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs leading-6 text-slate-500 dark:text-slate-400">
                    Les champs sont obligatoires pour permettre une réponse de la
                    mairie.
                  </p>

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="group inline-flex min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-6 text-sm font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-[#0f659d] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {status === "loading" ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <FiSend className="h-4 w-4 transition group-hover:translate-x-0.5" />
                        Envoyer le message
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}